<template>
  <div class="h-full p-6 overflow-y-auto w-full">
    <form
      class="space-y-6"
      @submit.prevent="submit"
    >
      <div class="space-y-4">
        <h3 class="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          {{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.performanceSecurity') }}
        </h3>

        <div class="grid grid-cols-2 gap-3">
          <button
            type="button"
            class="btn text-sm py-3"
            :class="[performanceSecurityForm.form.performance_security === 'retention' ? 'btn--primary-blue' : 'btn--secondary-blue']"
            @click="performanceSecurityForm.form.performance_security = 'retention'"
          >
            {{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.retention') }}
          </button>

          <button
            type="button"
            class="btn text-sm py-3"
            :class="[performanceSecurityForm.form.performance_security === 'guarantee' ? 'btn--primary-blue' : 'btn--secondary-blue']"
            @click="performanceSecurityForm.form.performance_security = 'guarantee'"
          >
            {{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.performanceGuarantee') }}
          </button>
        </div>
      </div>

      <!-- Liquidated Damages -->
      <div class="space-y-4">
        <h3 class="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          {{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.liquidatedDamagesRate') }}
        </h3>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.liquidatedDamagesRate') }}</label>

          <AppCurrencyInput
            v-model="performanceSecurityForm.form.liquidated_damages_value"
            name="liquidated_damages_value"
            :error="performanceSecurityForm.getErrors('liquidated_damages_value')"
            class="form-input"
            placeholder="Enter rate..."
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.liquidatedDamagesPeriod') }}</label>

          <AppSelect
            v-model="performanceSecurityForm.form.liquidated_damages_rate"
            name="liquidated_damages_rate"
            :error="performanceSecurityForm.getErrors('liquidated_damages_rate')"
            class="form-input capitalize"
            :options="['day', 'week', 'month']"
          />
        </div>

        <div
          v-if="performanceSecurityForm.form.performance_security === 'retention'"
          class="space-y-4 pt-2"
        >
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Claim Retention Percent
              <span class="text-xs text-gray-500">(percentage of certified amount per claim)</span>
            </label>

            <AppInput
              v-model="performanceSecurityForm.form.claim_retention_percent"
              :error="performanceSecurityForm.getErrors('claim_retention_percent')"
              class="form-input"
              type="number"
              min="0"
              max="100"
              step="0.01"
              name="claim_retention_percent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Total Retention Percent
              <span class="text-xs text-gray-500">(percentage of total awarded value)</span>
            </label>

            <AppInput
              v-model="performanceSecurityForm.form.total_retention_percent"
              :error="performanceSecurityForm.getErrors('total_retention_percent')"
              class="form-input"
              type="number"
              min="0"
              max="100"
              step="0.01"
              name="total_retention_percent"
            />
          </div>
        </div>
      </div>

      <!-- Save Button -->
      <div class="pt-4 border-t border-gray-200">
        <button
          v-if="canSave"
          type="submit"
          class="btn btn--primary-blue flex items-center space-x-2 w-full justify-center"
          :disabled="updateAction.is(States.LOADING)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

          <span>Save Performance Security</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import Package from '@/models/Package';
import { usePackagesStore } from '@/store/packages';
import { watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  selectedPackage: Package;
  canSave: boolean;
}>();

const packagesStore = usePackagesStore();
const updateAction = useStoreApiAction(packagesStore.update);

const performanceSecurityForm = useFormData({
  performance_security: 'retention',
  liquidated_damages_value: undefined,
  liquidated_damages_rate: undefined,
  claim_retention_percent: undefined,
  total_retention_percent: undefined,
});

watch(() => props.selectedPackage, (pkg) => {
  if(pkg) {
    performanceSecurityForm.setData(pkg);
  }
}, { immediate: true });

function submit() {
  performanceSecurityForm.resetErrors();

  updateAction
    .request(
      props.selectedPackage.id,
      performanceSecurityForm.form,
    )
    .then(() => {
      toast.success(t('Subcontract.PackagesDashboard.PackageBreakdown.message.packageDetailsUpdated'));
    })
    .catch((err) => {
      performanceSecurityForm.setErrors(err.data);
    });
}
</script>
